import React, { useEffect, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import API from '../ApiInterface';
import {
  inputClass,
  secondaryButtonClass,
  shellClass,
  tableHeaderClass,
  tableShellClass,
} from './ui';

const formatPacificDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(date);
};

const ManagePartnerApplicationUsers = (props) => {
  const application = props?.location?.state?.application || null;
  const token =
    props?.location?.state?.userInfo?.token ||
    props?.location?.state?.token ||
    '';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const PAGE_SIZE = 50;

  const goBack = () => {
    props.history.push({
      pathname: '/manage-partners',
      state: props?.location?.state?.returnState || props?.location?.state,
    });
  };

  useEffect(() => {
    let isMounted = true;

    const fetchUsers = async () => {
      if (!token || !application?.applicationId) {
        if (isMounted) {
          setError('Missing token or application data.');
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError('');
      const response = await API.getApplicationUsers(
        token,
        application.applicationId,
        PAGE_SIZE,
        page * PAGE_SIZE,
        searchEmail
      );

      if (!isMounted) return;

      if (response.status >= 200 && response.status < 300) {
        let fetchedUsers = [];
        if (Array.isArray(response.data?.users)) {
          fetchedUsers = response.data.users;
        } else if (response.data?.userId) {
          fetchedUsers = [response.data];
        }

        setUsers(fetchedUsers);
        setHasNextPage(!searchEmail && fetchedUsers.length === PAGE_SIZE);
      } else {
        setError(response?.data?.errorDescription || response?.data?.message || 'Failed to load application users.');
      }

      setLoading(false);
    };

    fetchUsers();

    return () => {
      isMounted = false;
    };
  }, [application, token, page, searchEmail]);

  const handleSearch = () => {
    const normalized = searchInput.trim();
    setPage(0);
    setSearchEmail(normalized);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchEmail('');
    setPage(0);
  };

  return (
    <div className={shellClass}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Application Users</h2>
          <p className="text-sm text-slate-600">{application?.name || '-'}</p>
        </div>
        <button
          type="button"
          onClick={goBack}
          className={secondaryButtonClass}
        >
          Back
        </button>
      </div>

      <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full max-w-sm">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
              Search by Email
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSearch();
                }}
                placeholder="user@example.com"
                className={inputClass}
              />
              <IconButton
                aria-label="Search users"
                onClick={handleSearch}
                className="!rounded-xl !border !border-slate-300 !bg-white !p-2.5 !text-slate-700 hover:!bg-slate-100"
              >
                <SearchIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label="Clear search"
                onClick={handleClear}
                className="!rounded-xl !border !border-slate-300 !bg-white !p-2.5 !text-slate-700 hover:!bg-slate-100"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          Loading application users...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className={tableShellClass}>
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className={tableHeaderClass}>
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">User ID</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Email</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Authorized</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Scopes</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Created At</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Updated At</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-700">Deleted At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {users.map((user) => (
                <tr key={`${user.userId}-${user.email}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs text-slate-700">{user.userId || '-'}</td>
                  <td className="px-3 py-2 text-slate-900">{user.email || '-'}</td>
                  <td className="px-3 py-2 text-slate-700">{user.authorized ? 'true' : 'false'}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {Array.isArray(user?.details?.scopes) && user.details.scopes.length > 0
                      ? user.details.scopes.join(', ')
                      : '-'}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">{formatPacificDateTime(user?.details?.createdAt)}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{formatPacificDateTime(user?.details?.updatedAt)}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{formatPacificDateTime(user?.details?.deletedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-500">No application users found.</div>
          )}
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
            <div className="text-xs text-slate-600">
              {searchEmail
                ? `${users.length} result(s) for "${searchEmail}"`
                : `Page ${page + 1} • ${users.length} users`}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                disabled={loading || page === 0 || !!searchEmail}
                className={secondaryButtonClass}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={loading || !hasNextPage || !!searchEmail}
                className={secondaryButtonClass}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagePartnerApplicationUsers;
